import { getInjector, getRootScope } from '@/services/angularjs-bridge';

const REVIEW_STATUSES = ['APPROVED', 'REVIEWED', 'REJECTED', 'RECEIVED'] as const;

const CHOOSE_STATUS_VALUE = '__choose__';

type ReviewStatus = (typeof REVIEW_STATUSES)[number];

interface ReviewAttribute {
    name: string;
    defaultValue?: unknown;
    description?: string;
    translatedDescription?: string;
    params?: {
        defaultValue?: unknown;
        description?: string;
    };
}

interface TemplateValidation {
    level: string;
    translatedMessage: string;
}

interface TemplateValidationInput {
    attributeName?: string;
    level?: string;
    message?: string;
    translatedMessage?: string;
}

interface ItemReviewTemplate {
    reviewStatus?: string;
    comment?: string;
}

function isReviewStatus(value: string | undefined | null): value is ReviewStatus {
    return !!value && (REVIEW_STATUSES as readonly string[]).includes(value);
}

function isPresentReviewValue(value: unknown): boolean {
    if (value == null) {
        return false;
    }
    if (typeof value === 'string') {
        return value.length > 0;
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (typeof value === 'object') {
        return Object.keys(value as object).length > 0;
    }
    return true;
}

function isReviewReadyToSend(status: string | undefined | null, comment: string | undefined | null): boolean {
    return isReviewStatus(status) && typeof comment === 'string' && comment.length > 0;
}

function reviewAttributeDefault(attribute: ReviewAttribute): unknown {
    const value = attribute.defaultValue !== undefined ? attribute.defaultValue : attribute.params?.defaultValue;
    return isPresentReviewValue(value) ? value : undefined;
}

function toTemplateValidations(
    sources: Array<Record<string, TemplateValidationInput[] | undefined> | undefined>,
    translateMessage?: (validation: TemplateValidationInput) => string | undefined,
): Record<string, TemplateValidation[]> {
    const result: Record<string, TemplateValidation[]> = {};
    const seen = new Set<string>();

    sources.forEach((source) => {
        if (!source) {
            return;
        }
        Object.entries(source).forEach(([key, list]) => {
            if (!Array.isArray(list)) {
                return;
            }
            list.forEach((validation) => {
                if (!validation) {
                    return;
                }
                const attributeName = validation.attributeName || key;
                const translatedMessage = validation.translatedMessage
                    || translateMessage?.(validation)
                    || validation.message
                    || '';
                const level = validation.level || '';
                if (!translatedMessage && !level) {
                    return;
                }
                const signature = `${attributeName}|${level}|${translatedMessage}`;
                if (seen.has(signature)) {
                    return;
                }
                seen.add(signature);
                if (!result[attributeName]) {
                    result[attributeName] = [];
                }
                result[attributeName].push({ level, translatedMessage });
            });
        });
    });

    return result;
}

function resolveTemplateValidations(
    validationErrors: Record<string, TemplateValidationInput[] | undefined> | undefined,
    validationWarnings: Record<string, TemplateValidationInput[] | undefined> | undefined,
    itemValidations: Record<string, TemplateValidationInput[] | undefined> | undefined,
    translateMessage?: (validation: TemplateValidationInput) => string | undefined,
): Record<string, TemplateValidation[]> {
    const fromStore = toTemplateValidations([validationErrors, validationWarnings], translateMessage);
    if (Object.keys(fromStore).length > 0) {
        return fromStore;
    }
    return toTemplateValidations([itemValidations], translateMessage);
}

function translateValidationMessage(validation: TemplateValidationInput): string | undefined {
    try {
        const $rootScope = getRootScope(getInjector());
        if (typeof $rootScope.translateValidationMessage === 'function') {
            const translated = $rootScope.translateValidationMessage(validation);
            return typeof translated === 'string' ? translated : undefined;
        }
    } catch {
        // No Angular shell (unit tests, Storybook).
    }
    return undefined;
}

function readItemReviewTemplate(
    item: unknown,
    validations: Record<string, TemplateValidation[]>,
): ItemReviewTemplate {
    try {
        const $rootScope = getRootScope(getInjector());
        const customService = typeof $rootScope.getService === 'function'
            ? $rootScope.getService('CustomItemReviewSettings')
            : undefined;
        if (!customService || typeof customService.itemReviewTemplate !== 'function') {
            return {};
        }
        const review = customService.itemReviewTemplate(
            item,
            validations,
            $rootScope.user,
            $rootScope.organization,
        ) || {};
        return {
            reviewStatus: typeof review.reviewStatus === 'string' ? review.reviewStatus : undefined,
            comment: typeof review.comment === 'string' ? review.comment : undefined,
        };
    } catch {
        return {};
    }
}

function buildInitialReviewValues({
    attributes,
    template,
}: {
    attributes: ReviewAttribute[];
    template?: ItemReviewTemplate | null;
}): Record<string, unknown> {
    const status = template?.reviewStatus;
    const values: Record<string, unknown> = {
        reviewStatus: isReviewStatus(status) ? status : '',
        reviewComment: template?.comment || '',
    };
    attributes.forEach((attribute) => {
        const value = reviewAttributeDefault(attribute);
        if (value !== undefined) {
            values[attribute.name] = value;
        }
    });
    return values;
}

function buildReviewRequest(
    primaryKey: string,
    reviewStatus: string,
    comment: string,
    attributes: ReviewAttribute[],
    values: Record<string, unknown>,
) {
    const customValues: Record<string, unknown> = {};
    attributes.forEach((attribute) => {
        const value = values[attribute.name];
        if (isPresentReviewValue(value)) {
            customValues[attribute.name] = value;
        }
    });
    return {
        primaryKey,
        reviewStatus,
        comment,
        customValues,
    };
}

function reviewAttributeDescription(
    attribute: ReviewAttribute,
    translate: (key: string) => string,
    translateAttributeDescription?: (attribute: ReviewAttribute) => string | null | undefined,
): string | undefined {
    if (typeof attribute.translatedDescription === 'string' && attribute.translatedDescription.trim()) {
        return attribute.translatedDescription;
    }
    const fromAngular = translateAttributeDescription?.(attribute);
    if (typeof fromAngular === 'string' && fromAngular.trim()) {
        return fromAngular;
    }
    const raw = attribute.description || attribute.params?.description;
    if (typeof raw === 'string' && raw.trim()) {
        return raw;
    }
    const key = `attribute.${attribute.name}.description`;
    const translated = translate(key);
    if (typeof translated === 'string' && translated.trim() && translated !== key) {
        return translated;
    }
    return undefined;
}

function showAttributeDescriptionCallout(attributeName: string, message: string) {
    try {
        const $rootScope = getRootScope(getInjector());
        if (typeof $rootScope.callout === 'function') {
            $rootScope.callout(attributeName, message);
        }
    } catch {
        // No Angular shell.
    }
}

export {
    buildInitialReviewValues,
    buildReviewRequest,
    CHOOSE_STATUS_VALUE,
    isReviewReadyToSend,
    readItemReviewTemplate,
    resolveTemplateValidations,
    REVIEW_STATUSES,
    type ReviewAttribute,
    reviewAttributeDefault,
    reviewAttributeDescription,
    showAttributeDescriptionCallout,
    type TemplateValidation,
    type TemplateValidationInput,
    translateValidationMessage,
};
